import {
  CHANGE_ANSWER,
  CHANGE_TREE_SELECT_CHOICES,
  REQUEST_CHANGE_CURRENT_SURVEY_STEP,
  REQUEST_FETCH_INITIAL_SURVEY_STEP,
} from 'actions/actionTypes';
import { setXCSRFToken } from 'actions/appActions';
import * as FlashMessageAction from 'actions/flashMessageActions';
// import actions
import * as Action from 'actions/survey/actions';
import * as API from 'apis/survey';
// import constants
import { FLASH_MESSAGE_TEXTS } from 'constants/flashMessages';
import { call, fork, put, select, take } from 'redux-saga/effects';
// import types
import {
  ChangeTreeSelectChoicesParamsTypes,
  SurveyOutlineTypes,
  SurveyState,
  SurveyStepItemTypes,
} from 'types/survey';
import { CallEffectInterface, createLazily } from '../index';

// Phase1で一時的にpreviewを対応する必要があるため、paramを取得
import 'core-js/features/url-search-params';

const queryParams = new URLSearchParams(window.location.search);
const isPreview = String(queryParams.get('preview')) === 'true';

// 初回Data取得
export function* runRequestFetchInitialSurveyStep() {
  const { deviceVariant } = yield select((state) => state.app);
  const { payload, newXCSRFToken, error }: CallEffectInterface<SurveyOutlineTypes> = yield call(
    API.fetchInitialSurveyStep,
  );

  if (payload && newXCSRFToken && !error) {
    if (isPreview) {
      yield put(
        FlashMessageAction.addFlashMessage({
          type: 'info',
          message: FLASH_MESSAGE_TEXTS.previewInfo,
          isRemained: true,
        }),
      );
    }
    yield put(Action.successFetchInitialSurveyStep(payload, deviceVariant));
    yield put(setXCSRFToken(newXCSRFToken));
  } else {
    yield put(Action.failureFetchInitialSurveyStep());
  }
}

function* handleRequestFetchInitialSurveyStep() {
  while (true) {
    yield take(REQUEST_FETCH_INITIAL_SURVEY_STEP);
    yield fork(runRequestFetchInitialSurveyStep);
  }
}

export function* runChangeCurrentSurveyStep(currentStepNumber: number) {
  yield put(Action.successChangeCurrentSurveyStep(currentStepNumber));
  yield put(Action.resetErrorToast());
}

// SurveyStep Data取得
export function* runFetchSurveyStep(currentStepNumber: number) {
  const { deviceVariant } = yield select((state) => state.app);
  const { surveySteps } = yield select((state) => state.survey);
  const lazily = createLazily(400);

  // currentSurveyStepのデータが無い場合、FETCH
  if (!surveySteps[currentStepNumber - 1]) {
    yield put(Action.requestFetchSurveyStep());
    const { payload, newXCSRFToken, error }: CallEffectInterface<SurveyOutlineTypes> = yield call(
      API.fetchSurveyStep,
      currentStepNumber,
    );

    if (payload && newXCSRFToken && !error) {
      yield put(Action.successFetchSurveyStep(currentStepNumber, payload, deviceVariant));
      yield put(setXCSRFToken(newXCSRFToken));
      yield fork(lazily, runChangeCurrentSurveyStep, currentStepNumber);
    } else {
      yield put(Action.failureFetchSurveyStep());
    }
  } else {
    yield fork(lazily, runChangeCurrentSurveyStep, currentStepNumber);
  }
}

export function* runPostAnswer(currentStepNumber: number) {
  const { deviceVariant } = yield select((state) => state.app);
  const survey: SurveyState = yield select((state) => state.survey);
  const { currentSurveyStep } = survey;
  const { xCSRFToken } = yield select((state) => state.app);
  const lazily = createLazily(400);
  const oldStepNumber = currentSurveyStep.stepNumber;
  const toNext = oldStepNumber < currentStepNumber;

  const isQuestion = (surveyStepItem: SurveyStepItemTypes) => {
    return surveyStepItem.stepItemType !== 'instruction';
  };

  const hasQuestion = currentSurveyStep.surveyStepItems.some((surveyStepItem) =>
    isQuestion(surveyStepItem),
  );

  if (toNext && hasQuestion) {
    yield put(Action.requestPostAnswers());
    const answers = currentSurveyStep.surveyStepItems.map((surveyStepItem: SurveyStepItemTypes) => {
      return surveyStepItem.answer;
    });
    const { payload, newXCSRFToken, error }: CallEffectInterface<SurveyOutlineTypes> = yield call(
      API.postAnswers,
      answers,
      xCSRFToken,
    );
    if (payload && newXCSRFToken && !error) {
      yield put(Action.successPostAnswers(currentStepNumber, payload, deviceVariant));
      yield put(setXCSRFToken(newXCSRFToken));
      yield fork(lazily, runChangeCurrentSurveyStep, currentStepNumber);
    } else {
      yield put(Action.failurePostAnswers());
    }
  } else {
    yield fork(runFetchSurveyStep, currentStepNumber);
  }
}

export function* runPostCompleteSurvey(currentStepNumber: number) {
  const { xCSRFToken } = yield select((state) => state.app);

  yield put(Action.requestPostCompleteSurvey());
  const { payload, error } = yield call(API.postCompleteSurvey, xCSRFToken);

  if (payload && !error) {
    yield put(Action.successPostCompleteSurvey());
    yield fork(runFetchSurveyStep, currentStepNumber);
  } else {
    yield put(Action.failurePostCompleteSurvey());
  }
}

export function* runSlideAnimation(currentStepNumber: number) {
  const { currentSurveyStep } = yield select((state) => state.survey);
  // Phase1で一時的にpreviewを対応する必要があるため、paramを取得
  const param: string = yield select((state) => state.router.location.search.replace(/^\?/, ''));

  const oldStepNumber = currentSurveyStep.stepNumber;
  const toNext = oldStepNumber < currentStepNumber;

  if (toNext) {
    yield put(Action.slideNextStep());
  } else {
    yield put(Action.slidePrevStep());
  }

  if (param.match(/preview/)) {
    yield fork(runFetchSurveyStep, currentStepNumber);
  } else if (toNext && currentSurveyStep.stepType === 'confirmation') {
    yield fork(runPostCompleteSurvey, currentStepNumber);
  } else {
    yield fork(runPostAnswer, currentStepNumber);
  }
}

// 回答があるか検証
export function* runVerifyCurrentSurveyStep(currentStepNumber: number) {
  const { currentSurveyStep } = yield select((state) => state.survey);

  const oldStepNumber = currentSurveyStep.stepNumber;
  const toNext = oldStepNumber < currentStepNumber;

  const hasInvalidAnswer = currentSurveyStep.surveyStepItems.some(
    (surveyStepItem: SurveyStepItemTypes) => {
      return surveyStepItem.stepItemType !== 'instruction' && !surveyStepItem.valid;
    },
  );
  // 次のsurveyStepに移動する場合で、回答が無い場合はエラーメッセージを表示
  if (toNext && hasInvalidAnswer) {
    yield put(Action.hasInvalidAnswer());
  } else {
    yield fork(runSlideAnimation, currentStepNumber);
  }
}

// 「はじめる」「前へ戻る」「次に進む」により実行
function* handleChangeCurrentSurveyStep() {
  while (true) {
    const { currentStepNumber } = yield take(REQUEST_CHANGE_CURRENT_SURVEY_STEP);
    yield fork(runVerifyCurrentSurveyStep, currentStepNumber);
  }
}

export function* runChangeTreeSelectChoicesBySearch(params: ChangeTreeSelectChoicesParamsTypes) {
  yield put(Action.requestFetchTreeSelectChoices());
  const { keyword } = params;

  const { payload, error } = yield call(API.getSearchChoices, keyword);

  if (payload && !error) {
    yield put(Action.successFetchTreeSelectChoices(params.surveyStepItemId, payload));
  } else {
    yield put(Action.failureFetchTreeSelectChoices());
  }
}

export function* runChangeTreeSelectChoicesByDive(params: ChangeTreeSelectChoicesParamsTypes) {
  yield put(Action.requestFetchTreeSelectChoices());
  const { targetId } = params;

  const { payload, error } = yield call(API.getDiveChoices, targetId);

  if (payload && !error) {
    yield put(Action.successFetchTreeSelectChoices(params.surveyStepItemId, payload));
  } else {
    yield put(Action.failureFetchTreeSelectChoices());
  }
}

// stepType: 'tree_select'の場合、選択肢をAPIにより変更
function* handleChangeTreeSelectChoices() {
  const lazily = createLazily(400);
  while (true) {
    const { params } = yield take(CHANGE_TREE_SELECT_CHOICES);

    if (params.selectType === 'search') {
      yield fork(lazily, runChangeTreeSelectChoicesBySearch, params);
    } else {
      yield fork(runChangeTreeSelectChoicesByDive, params);
    }
  }
}

// 回答に変更があった場合、有効な回答か検証する
function* handleChangeAnswer() {
  while (true) {
    yield take(CHANGE_ANSWER);
    yield put(Action.verifyAnswer());
  }
}

export default function* surveySaga() {
  yield fork(handleRequestFetchInitialSurveyStep);
  yield fork(handleChangeCurrentSurveyStep);
  yield fork(handleChangeTreeSelectChoices);
  yield fork(handleChangeAnswer);
}
